<template>
    <div class="uxfolio-app-root">
        <RouterView v-slot="{ Component }">
                <component
                    :is="Component"
                />
        </RouterView>
        <ImageZoom v-if="$store.state.zoomData.isZoomed" />
        <AuthStatusModal
            :show="authStatus.show"
            :title="authStatus.title"
            :message="authStatus.message"
            :primary-label="authStatus.primaryLabel"
            :secondary-label="authStatus.secondaryLabel"
            :busy="authStatus.busy"
            @primary="handleAuthStatusAction(authStatus.primaryAction)"
            @secondary="handleAuthStatusAction(authStatus.secondaryAction)"
        />
    </div>
</template>

<script>
    import ImageZoom from "@/scripts/common/ImageZoom.vue";
    import { useHead } from "@unhead/vue";
    import { useRoute } from "vue-router";
    import AuthStatusModal from "@/scripts/portfolioEditor/modals/AuthStatusModal.vue";
    import {AUTH_STATUS_CLEAR_EVENT, AUTH_STATUS_EVENT, PERMISSION_DENIED_EVENT, SESSION_EXPIRED_EVENT, suppressAuthStatusForRequest} from "@/scripts/auth/authStatus.js";
    import {isEditorViewModeRoute, login, logout, requiresAuthentication} from "@/scripts/auth/keycloakAuth.js";

    export default {
        name: "App",
        components: {
            ImageZoom,
            AuthStatusModal,
        },
        data() {
            return {
                authStatus: {
                    show: false,
                    title: '',
                    message: '',
                    primaryLabel: null,
                    primaryAction: null,
                    secondaryLabel: null,
                    secondaryAction: null,
                    busy: false,
                    redirectUri: null,
                },
                authStatusTimer: null,
            }
        },
        setup() {
            const route = useRoute();
            const siteUrl = "https://uxfol.io";
            const ogImage = `${siteUrl}/landing-images/og_image.png`;
            const logoUrl = `${siteUrl}/landing-images/uxfolio-logo.svg`;
            // Only set UXfolio metadata on UXfolio hosts skipping owndomains
            const isUxfolioHost = window.location.hostname === "uxfol.io"
                || window.location.hostname.endsWith(".uxfol.io");
            const portfolioGeneratorPath = "/ux-portfolio-generator";

            const pageMeta = {
                '/': {
                    title: 'UXfolio - Build the UX portfolio that gets you hired',
                    description: 'Build your UX portfolio faster with supercharged tools tailored to your needs and hiring managers\' expectations.'
                },
                '/pricing': {
                    title: 'Pricing and Plans - UXfolio',
                    description: 'Check our our Free and Premium plans and start building your online portfolio today! You can easily switch between UXfolio plans any time.'
                },
                '/showcase': {
                    title: 'UX Case Study Showcase - UXfolio',
                    description: 'Need some inspiration? Check out the best case studies created with UXfolio and tell your own design story!'
                },
                '/examples': {
                    title: 'UXfolio portfolio & case study examples',
                    description: 'Explore a curated collection of inspiring portfolio and case study examples crafted by UXfolio users.'
                },
                '/privacy-policy': {
                    title: 'Privacy Policy - UXfolio',
                    description: 'Read UXfolio’s privacy policy to understand how we collect, use, protect, and handle your personal information.'
                },
                '/terms-and-conditions': {
                    title: 'Terms and Conditions - UXfolio',
                    description: 'Read the UXfolio terms of service, subscription terms, and usage conditions.'
                },
                '/ux-portfolio-generator/terms-and-conditions': {
                    title: 'AI Portfolio Generator Terms and Conditions - UXfolio',
                    description: 'Read the terms for UXfolio’s AI Portfolio Generator: how your files are used, stored, and deleted, Figma access, and your responsibilities.'
                },
                '/imprint': {
                    title: 'Imprint - UXfolio',
                    description: 'Company and contact information for UXfolio.'
                },
                '/partnership-program/thank-you': {
                    title: 'Partnership program - thank you - UXfolio',
                    description: 'Thanks for applying to the UXfolio partnership program.'
                },
                '/custom-ux-portfolio': {
                    title: 'UXfolio - Build the UX portfolio that gets you hired',
                    description: 'Build your UX portfolio faster with supercharged tools tailored to your needs and hiring managers\' expectations.'
                },
                [portfolioGeneratorPath]: {
                    title: 'UX Portfolio Generator - UXfolio',
                    description: 'Turn your existing UX design work and source material into an editable UX portfolio.',
                    social: true
                },
                '/login': {
                    title: 'Log in - UXfolio',
                    metaTitle: 'Log in to UXfolio',
                    description: 'Log in to your UXfolio account to continue building your UX portfolio'
                },
                '/signup': {
                    title: 'Sign up - UXfolio',
                    metaTitle: 'Sign up to UXfolio',
                    description: 'Sign up to UXfolio and build the UX portfolio that gets you hired'
                },
                '/forgot-password': {
                    title: 'Reset password - UXfolio',
                    metaTitle: 'Reset your UXfolio password',
                    description: 'Reset your UXfolio password to regain access to your UX portfolio'
                },
                '/reset-password': {
                    title: 'Create new password - UXfolio',
                    metaTitle: 'Create a new UXfolio password',
                    description: 'Create a new password for your UXfolio account to continue building your UX portfolio'
                },
            };

            const isPortfolioGeneratorPath = (path) =>
                path === portfolioGeneratorPath || path.startsWith(`${portfolioGeneratorPath}/`);
            const canonicalPagePath = (path) => isPortfolioGeneratorPath(path) ? portfolioGeneratorPath : path;
            const getPageMeta = (path) => pageMeta[canonicalPagePath(path)] || null;

            const buildJsonLd = (path) => {
                const currentUrl = `${siteUrl}${canonicalPagePath(path)}`;
                const meta = getPageMeta(path);
                const metaTitle = meta.metaTitle || meta.title;
                const schema = [];

                if (path === '/') {
                    schema.push({
                        key: 'jsonld-organization',
                        type: 'application/ld+json',
                        children: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'Organization',
                            name: 'UXfolio',
                            url: siteUrl,
                            logo: logoUrl,
                            description: meta.description
                        })
                    });
                    schema.push({
                        key: 'jsonld-website',
                        type: 'application/ld+json',
                        children: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'WebSite',
                            name: 'UXfolio',
                            url: siteUrl,
                            description: meta.description
                        })
                    });
                }

                schema.push({
                    key: 'jsonld-webpage',
                    type: 'application/ld+json',
                    children: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'WebPage',
                        name: metaTitle,
                        description: meta.description,
                        url: currentUrl,
                        isPartOf: {
                            '@type': 'WebSite',
                            name: 'UXfolio',
                            url: siteUrl
                        }
                    })
                });

                return schema;
            };

            useHead(() => {
                const isAuthPage = ['/login', '/signup', '/forgot-password', '/reset-password'].includes(route.path);
                const meta = getPageMeta(route.path);
                const canonicalPath = canonicalPagePath(route.path);

                if ((!isUxfolioHost && !isAuthPage) || !meta) {
                    return {};
                }

                return {
                    title: meta.title,
                    meta: [
                        {
                            key: 'title',
                            name: 'title',
                            content: meta.metaTitle || meta.title
                        },
                        {
                            key: 'description',
                            name: 'description',
                            content: meta.description
                        },
                        {
                            key: 'og:title',
                            property: 'og:title',
                            content: meta.metaTitle || meta.title
                        },
                        {
                            key: 'og:description',
                            property: 'og:description',
                            content: meta.description
                        },
                        ...(meta.social ? [
                            {
                                key: 'og:type',
                                property: 'og:type',
                                content: 'website'
                            },
                            {
                                key: 'og:site_name',
                                property: 'og:site_name',
                                content: 'UXfolio'
                            },
                        ] : []),
                        {
                            key: 'og:image',
                            property: 'og:image',
                            content: ogImage
                        },
                        {
                            key: 'og:url',
                            property: 'og:url',
                            content: `${siteUrl}${canonicalPath}`
                        },
                        ...(meta.social ? [
                            {
                                key: 'twitter:card',
                                name: 'twitter:card',
                                content: 'summary_large'
                            },
                            {
                                key: 'twitter:site',
                                name: 'twitter:site',
                                content: '@UXfolio'
                            },
                            {
                                key: 'twitter:title',
                                name: 'twitter:title',
                                content: meta.metaTitle || meta.title
                            },
                            {
                                key: 'twitter:description',
                                name: 'twitter:description',
                                content: meta.description
                            },
                            {
                                key: 'twitter:image',
                                name: 'twitter:image',
                                content: ogImage
                            }
                        ] : [])
                    ],
                    link: [
                        {
                            key: "canonical",
                            rel: "canonical",
                            href: `${siteUrl}${canonicalPath}`,
                        }
                    ],
                    script: buildJsonLd(route.path)
                };
            });
        },
        methods: {
            showAuthStatus(detail) {
                if (this.authStatusTimer) {
                    clearTimeout(this.authStatusTimer);
                    this.authStatusTimer = null;
                }

                this.authStatus = {
                    show: true,
                    title: detail.title,
                    message: detail.message,
                    primaryLabel: detail.primaryLabel || null,
                    primaryAction: detail.primaryAction || null,
                    secondaryLabel: detail.secondaryLabel || null,
                    secondaryAction: detail.secondaryAction || null,
                    busy: Boolean(detail.busy),
                    redirectUri: detail.redirectUri || window.location.href,
                };

                if (detail.state === 'checking_sso') {
                    this.authStatusTimer = setTimeout(() => {
                        this.authStatus = {
                            ...this.authStatus,
                            message: 'This is taking longer than usual. You can wait a little more, try again, or open the secure login page.',
                            primaryLabel: 'Try again',
                            primaryAction: 'retry',
                            secondaryLabel: 'Open login',
                            secondaryAction: 'forceLogin',
                            busy: false,
                        };
                    }, 8000);
                }
            },
            clearAuthStatus() {
                if (this.authStatusTimer) {
                    clearTimeout(this.authStatusTimer);
                    this.authStatusTimer = null;
                }

                this.authStatus = {
                    show: false,
                    title: '',
                    message: '',
                    primaryLabel: null,
                    primaryAction: null,
                    secondaryLabel: null,
                    secondaryAction: null,
                    busy: false,
                    redirectUri: null,
                };
            },
            showSessionExpired(detail = {}) {
                if (!this.shouldShowSessionExpiredOverlay(detail)) {
                    return;
                }

                this.showAuthStatus({
                    title: 'Session expired',
                    message: 'Your session has expired due to inactivity. Please log in again to continue editing.',
                    primaryLabel: 'Log in again',
                    primaryAction: 'login',
                    secondaryLabel: 'Go to homepage',
                    secondaryAction: 'goHome',
                    redirectUri: window.location.href,
                });
            },
            shouldShowSessionExpiredOverlay(detail = {}) {
                if (suppressAuthStatusForRequest(detail.url)) {
                    return false;
                }

                const pathname = this.$route?.path || window.location.pathname;

                return requiresAuthentication(pathname) || isEditorViewModeRoute(pathname);
            },
            showPermissionDenied(detail = {}) {
                this.showAuthStatus({
                    title: detail.title || 'Access not available',
                    message: detail.message || 'You are signed in, but this account does not have access to that area.',
                    primaryLabel: detail.primaryLabel || 'Go to homepage',
                    primaryAction: detail.primaryAction || 'goHome',
                    secondaryLabel: detail.secondaryLabel || 'Use another account',
                    secondaryAction: detail.secondaryAction || 'retryLogout',
                    redirectUri: detail.redirectUri || window.location.origin,
                });
            },
            handleAuthStatusAction(action) {
                if (action === 'retry') {
                    window.location.reload();
                    return;
                }

                if (action === 'forceLogin') {
                    return login(this.authStatus.redirectUri || window.location.href, { skipSessionCheck: true });
                }

                if (action === 'login') {
                    return login(this.authStatus.redirectUri || window.location.href);
                }

                if (action === 'retryLogout') {
                    return logout(window.location.origin);
                }

                if (action === 'goHome') {
                    window.location.assign(window.location.origin);
                    return;
                }

                if (action === 'goToRedirectUri') {
                    window.location.assign(this.authStatus.redirectUri || window.location.origin);
                    return;
                }

                this.clearAuthStatus();
            },
        },
        mounted() {
            this._keyListener = function(e) {
                if (e.code === "KeyG" && e.shiftKey && e.ctrlKey) {
                    e.preventDefault();
                    this.$store.state.displayToggleGrid = !this.$store.state.displayToggleGrid;
                }
            }
            this._boundKeyListener = this._keyListener.bind(this);
            document.addEventListener('keydown', this._boundKeyListener);
            this._authStatusListener = (event) => this.showAuthStatus(event.detail || {});
            this._authStatusClearListener = () => this.clearAuthStatus();
            this._sessionExpiredListener = (event) => this.showSessionExpired(event.detail || {});
            this._permissionDeniedListener = (event) => this.showPermissionDenied(event.detail || {});
            window.addEventListener(AUTH_STATUS_EVENT, this._authStatusListener);
            window.addEventListener(AUTH_STATUS_CLEAR_EVENT, this._authStatusClearListener);
            window.addEventListener(SESSION_EXPIRED_EVENT, this._sessionExpiredListener);
            window.addEventListener(PERMISSION_DENIED_EVENT, this._permissionDeniedListener);
            if (window.__uxfolioAuthStatus) {
                this.showAuthStatus(window.__uxfolioAuthStatus);
            }
        },
        beforeUnmount() {
            document.removeEventListener('keydown', this._boundKeyListener);
            window.removeEventListener(AUTH_STATUS_EVENT, this._authStatusListener);
            window.removeEventListener(AUTH_STATUS_CLEAR_EVENT, this._authStatusClearListener);
            window.removeEventListener(SESSION_EXPIRED_EVENT, this._sessionExpiredListener);
            window.removeEventListener(PERMISSION_DENIED_EVENT, this._permissionDeniedListener);
            this.clearAuthStatus();
        },
    }
</script>

<style lang="scss" scoped>
    .uxfolio-app-root {
        display: contents;
    }
</style>
